// MARK://dhk8/pos/lib/bridge/bridge-client
// Cliente de impresión MODE-AWARE (per-terminal, printing-config-store.bridge):
//
//   - mode 'host'  → loopback 127.0.0.1:9101 (Bridge USB local — fast path histórico,
//     SIN CAMBIOS). El navegador y el bridge corren en el mismo equipo, sin
//     mixed-content, sin auth (CORS *).
//   - mode 'relay' → Print Relay (impresión por la nube): las impresiones NO hablan a
//     la LAN; se ENCOLAN en `POST …/print/jobs` (relay-client) y el Bridge HOST de la
//     estación las jala e imprime local. Esto evita el bloqueo de mixed-content (POS
//     https → IP LAN http). El JWT/tenant los pone apiFetch. Ver docs/print-relay-design.md.
//
// Endpoints loopback reales (NintaiGO.Bridge Program.cs, f0-prototype):
//   GET  /                             → home (texto, build viejo solo expone esto)
//   GET  /v1/version                   → { name, version, channel, paired, os, platform, capabilities }
//   GET  /v1/printers                  → { printers: [{ name, isDefault?, isOnline? }] }
//   POST /v1/print?printer=…&jobName=… → body raw bytes (octet-stream) — ESC/POS pre-rendereado
//   POST /v1/print/receipt?printer=…   → body JSON CanonicalReceipt — bridge convierte a ESC/POS
//   POST /v1/drawer/kick?printer=…     → body { pin: 2|5 }
//   GET  /v1/test-page                 → HTML embebido (test directo a impresora default)
//
// Detección "bridge desactualizado" (solo loopback): `/v1/version` da 404 (build sin
// endpoints nuevos) pero `/` responde 200 → el servicio corre, falta upgrade.
//
// En mode 'relay' la operación es asíncrona: printReceipt/printRaw/kickDrawer
// devuelven un PrintResult con `jobId` apenas el job entra a la cola (no esperan a
// que el papel salga). El seguimiento (enviado→imprimiendo→impreso/falló) se hace
// con relay-client.getJob desde la UI (PrintingSettingsPage). getStatus() en relay
// deriva online/offline del heartbeat de la estación.

import { getPrintingConfig } from './printing-config-store.js'
import {
  enqueueJob as relayEnqueueJob,
  getStationStatus,
  type RelayJobKind,
} from './relay-client.js'

const LOOPBACK_BASE = 'http://127.0.0.1:9101'
const DEFAULT_TIMEOUT_MS = 1500

/** Loopback siempre 127.0.0.1:9101 — el modo 'host' habla SIEMPRE a la máquina
 *  local (el viejo modo 'client' LAN-directo se reemplazó por 'relay' vía nube,
 *  que no usa este base). */
function bridgeBase(): string {
  return LOOPBACK_BASE
}

/** ¿Esta terminal imprime vía nube (Print Relay)? Leído fresco en cada llamada. */
function isRelayMode(): boolean {
  return getPrintingConfig().bridge.mode === 'relay'
}

/** Lee la estación nube configurada; lanza si el modo es relay pero falta vincular. */
function requireStationId(): string {
  const { stationId } = getPrintingConfig().bridge
  if (!stationId) throw new Error('No hay estación de impresión vinculada — ve a Ajustes › Impresión › Vía nube')
  return stationId
}

/** Genera una idempotencyKey estable-por-intento para los enqueue del relay.
 *  Evita doble impresión si la red reintenta el POST. crypto.randomUUID cuando
 *  existe, fallback a timestamp+random. */
function newIdempotencyKey(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `${prefix}-${crypto.randomUUID()}`
  } catch { /* fallthrough */ }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

// ── tipos ────────────────────────────────────────────────────────────────────

export interface BridgeVersionPayload {
  name: string
  version: string
  channel: string
  paired: boolean
  os: string
  platform: 'win' | 'non-windows' | string
  capabilities: string[]
}

export type BridgeStatus =
  | { state: 'online'; version: BridgeVersionPayload }
  | { state: 'outdated'; reason: string }
  | { state: 'offline'; reason: string }
  // mode 'relay': estado derivado del heartbeat de la estación nube. `online`
  // refleja si el Bridge HOST está haciendo poll (last heartbeat < 20s, lo decide
  // el backend). `stationName`/`lastSeenAt` alimentan el panel de Ajustes.
  | { state: 'relay-online'; stationName: string; lastSeenAt?: string | null }
  | { state: 'relay-offline'; stationName?: string | null; lastSeenAt?: string | null; reason: string }

export interface BridgePrinter {
  name: string
  isDefault?: boolean
  isOnline?: boolean
}

export type PrintKind = 'receipt' | 'precuenta' | 'kitchen'

/** Payload que mandamos al endpoint POST /v1/print/receipt. El bridge lo
 *  convierte a ESC/POS internamente. Forma derivada del adapter del POS
 *  (lib/print/templates.ts) — todo opcional para que builds intermedios
 *  del bridge no truenen si falta un campo. */
/** Línea de texto pre-renderizada — el bridge la pinta literal con align/bold/size.
 *  Mismo shape que TextLine.cs en el bridge .NET. */
export interface TextLine {
  text?: string
  align?: 'left' | 'center' | 'right'
  bold?: boolean
  underline?: boolean
  /** 'normal' | 'double' | 'double-h' | 'double-w'. */
  size?: 'normal' | 'double' | 'double-h' | 'double-w'
  /** False = no append \n. Default true. */
  newline?: boolean
}

/** Item de venta — 1 línea con layout "qty × name ........ $amount". */
export interface ReceiptItem {
  qty: number
  name: string
  /** Monto en centavos (MXN minor units). */
  amountCents: number
}

/** Línea de total — "Label .......... $amount" (bold opcional). */
export interface ReceiptTotal {
  label: string
  amountCents: number
  bold?: boolean
}

export interface ReceiptQr {
  data: string
  /** Tamaño 1..16 (default 6). */
  size?: number
  /** Corrección de errores: L|M|Q|H (default M). */
  ecc?: 'L' | 'M' | 'Q' | 'H'
}

export interface ReceiptBarcode {
  data: string
  type?: 'CODE128' | 'EAN13' | 'CODE39'
  height?: number
  width?: number
  hri?: 'none' | 'above' | 'below' | 'both'
}

export interface ReceiptDrawer {
  /** 2 (común) o 5 (Asian-market). Default 2. */
  pin?: 2 | 5
}

/**
 * CanonicalReceipt — contract REAL del bridge .NET, confirmado en
 * /Users/gorlandodb/Projects/nintaigo-bridge/src/NintaiGO.Bridge/EscPos/CanonicalReceipt.cs.
 *
 * Shape pre-renderizado: el caller (PWA) debe construir Header/Footer como
 * TextLine[] (con align/bold/size), Items como {qty,name,amountCents},
 * Totals como [{label,amountCents,bold?}]. El bridge solo aplica ESC/POS
 * a esas instrucciones — NO conoce business/order/kind/fiscal/modifiers.
 *
 * Si necesitas data rica (modifiers inline, sub-items, fiscal block), expánde
 * a TextLine[] en el adapter (`canonical-receipt.ts`).
 */
export interface CanonicalReceipt {
  /** Codepage del .NET (default 'cp850'). MX usar 'cp858' para €/acentos. */
  encoding?: string
  /** Chars por línea (default 42 = 80mm font A). */
  width?: number
  /** Beep al inicio. */
  beep?: boolean
  /** Líneas centradas/bold/double-h antes del rule + items. */
  header?: TextLine[]
  /** Stream de líneas libres (raras veces usado — entre Header y Items). */
  lines?: TextLine[]
  /** Items con layout automático qty/name/amount. */
  items?: ReceiptItem[]
  /** Totales con layout label-amount. */
  totals?: ReceiptTotal[]
  /** Líneas finales (después del último rule). */
  footer?: TextLine[]
  /** Rule character (default '-'). */
  rule?: { char?: string }
  /** 'partial' | 'full' | 'none'. Default 'partial'. */
  cut?: 'partial' | 'full' | 'none'
  /** Si presente, abre el cajón después de imprimir. */
  cashdrawer?: ReceiptDrawer
  /** QR al pie (típicamente tickets.nintaigo.com/{uuid}). */
  qr?: ReceiptQr
  /** Código de barras opcional. */
  barcode?: ReceiptBarcode
}

export interface PrintResult {
  ok: boolean
  jobId?: string
  error?: string
}

// ── helpers ──────────────────────────────────────────────────────────────────

function withTimeout(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
    try { return (AbortSignal as unknown as { timeout: (n: number) => AbortSignal }).timeout(ms) } catch { /* fallthrough */ }
  }
  const ctl = new AbortController()
  setTimeout(() => { try { ctl.abort() } catch { /* noop */ } }, ms)
  return ctl.signal
}

async function bridgeFetch(path: string, init: RequestInit = {}, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<Response> {
  // Token opcional (mode 'client' con emparejamiento). Se mergea sobre los
  // headers del caller (p.ej. Content-Type) sin pisarlos.
  const token = getPrintingConfig().bridge.token
  const headers = token
    ? { ...(init.headers as Record<string, string> | undefined), 'x-nintaigo-bridge-token': token }
    : init.headers
  return fetch(`${bridgeBase()}${path}`, {
    ...init,
    ...(headers ? { headers } : {}),
    signal: init.signal ?? withTimeout(timeoutMs),
  })
}

function friendlyError(e: unknown): string {
  if (e instanceof DOMException && e.name === 'AbortError') return 'Sin respuesta (timeout)'
  if (e instanceof TypeError) return 'No se pudo conectar al bridge'
  if (e instanceof Error) return e.message
  return 'Error desconocido'
}

// ── status / detección ───────────────────────────────────────────────────────

async function pingHome(timeoutMs: number): Promise<boolean> {
  try {
    const res = await bridgeFetch('/', { method: 'GET' }, timeoutMs)
    return res.ok
  } catch { return false }
}

/** Diagnostica el bridge en 3 estados:
 *  - 'online'   → /v1/version devuelve 200 con payload válido.
 *  - 'outdated' → /v1/version no existe (404/405/500) pero / responde → falta upgrade.
 *  - 'offline'  → no hay servicio en 127.0.0.1:9101 (firewall, no instalado, detenido).
 *
 *  En mode 'relay' NO hay loopback que consultar: el estado se deriva del heartbeat
 *  de la estación nube (online = el Bridge HOST está haciendo poll). Mapeamos a
 *  'online'/'offline' para que la UI de status existente siga funcionando.
 */
export async function getStatus(timeoutMs = DEFAULT_TIMEOUT_MS): Promise<BridgeStatus> {
  if (isRelayMode()) return getRelayStatus()
  try {
    const res = await bridgeFetch('/v1/version', { method: 'GET' }, timeoutMs)
    if (res.ok) {
      const body = await res.json().catch(() => null) as Partial<BridgeVersionPayload> | null
      if (body && typeof body.version === 'string') {
        const version: BridgeVersionPayload = {
          name: body.name ?? 'NintaiGO Bridge',
          version: body.version,
          channel: body.channel ?? 'unknown',
          paired: !!body.paired,
          os: body.os ?? '',
          platform: body.platform ?? 'unknown',
          capabilities: Array.isArray(body.capabilities) ? body.capabilities : [],
        }
        return { state: 'online', version }
      }
      // 200 con payload inválido → outdated.
      return { state: 'outdated', reason: 'Respuesta de /v1/version sin formato válido' }
    }
    if (res.status === 404 || res.status === 405 || res.status === 500) {
      const home = await pingHome(timeoutMs).catch(() => false)
      if (home) return { state: 'outdated', reason: 'El bridge no expone /v1/version' }
      return { state: 'offline', reason: `HTTP ${res.status}` }
    }
    const home = await pingHome(timeoutMs).catch(() => false)
    if (home) return { state: 'outdated', reason: `HTTP ${res.status} en /v1/version` }
    return { state: 'offline', reason: `HTTP ${res.status}` }
  } catch (e) {
    const home = await pingHome(timeoutMs).catch(() => false)
    if (home) return { state: 'outdated', reason: 'No hay /v1/version (build viejo)' }
    return { state: 'offline', reason: friendlyError(e) }
  }
}

/** Estado relay: consulta el heartbeat de la estación vinculada. Si no hay
 *  estación → relay-offline con motivo accionable. */
async function getRelayStatus(): Promise<BridgeStatus> {
  const { stationId, stationName } = getPrintingConfig().bridge
  if (!stationId) {
    return { state: 'relay-offline', reason: 'No hay estación vinculada' }
  }
  try {
    const st = await getStationStatus(stationId)
    if (st.online) {
      return { state: 'relay-online', stationName: st.name || stationName || 'Estación', lastSeenAt: st.lastSeenAt ?? null }
    }
    return {
      state: 'relay-offline',
      stationName: st.name || stationName || null,
      lastSeenAt: st.lastSeenAt ?? null,
      reason: 'La estación no ha reportado heartbeat',
    }
  } catch (e) {
    return {
      state: 'relay-offline',
      stationName: stationName ?? null,
      reason: friendlyError(e),
    }
  }
}

// ── printers ─────────────────────────────────────────────────────────────────

export async function getPrinters(timeoutMs = DEFAULT_TIMEOUT_MS): Promise<BridgePrinter[]> {
  const res = await bridgeFetch('/v1/printers', { method: 'GET' }, timeoutMs)
  if (!res.ok) throw new Error(`Bridge devolvió HTTP ${res.status}`)
  const body = await res.json() as { printers?: BridgePrinter[] } | BridgePrinter[]
  if (Array.isArray(body)) return body
  return Array.isArray(body.printers) ? body.printers : []
}

/** Resuelve el nombre de la impresora a usar:
 *   1. `preferred` si viene seteado
 *   2. la impresora marcada `isDefault: true` en /v1/printers
 *   3. la primera de la lista
 *   4. null si la lista está vacía
 */
export async function resolveDefaultPrinter(preferred?: string | null): Promise<string | null> {
  if (preferred && preferred.trim().length > 0) return preferred
  const printers = await getPrinters().catch(() => [])
  if (printers.length === 0) return null
  const def = printers.find((p) => p.isDefault === true)
  return (def ?? printers[0])?.name ?? null
}

// ── test page ────────────────────────────────────────────────────────────────

/**
 * 2026-05-25: GET /v1/test-page del bridge SOLO retorna HTML (página
 * embebida para abrir manualmente en browser, NO imprime). Para que el
 * botón "Imprimir página de prueba" REALMENTE imprima, mandamos bytes
 * ESC/POS hardcodeados de un test breve al spooler via POST /v1/print.
 *
 * Bytes del test (32 chars-wide thermal):
 *   ESC @           inicializar
 *   ESC ! 56        font A bold + double height + double width
 *   "NINTAIGO\n"
 *   ESC ! 0         font normal
 *   "Bridge OK\n"
 *   "----------------\n"
 *   "EPSON TM-T20II\n"
 *   timestamp
 *   "\n\n\n"        feed
 *   GS V 0          full cut
 */
export async function testPage(opts: { printer?: string | null } = {}, timeoutMs = 5000): Promise<void> {
  const printer = await resolveDefaultPrinter(opts.printer ?? null)
  if (!printer) throw new Error('No hay impresora disponible para test de impresión')

  const enc = new TextEncoder()
  const parts: number[] = []
  // Init
  parts.push(0x1B, 0x40)
  // Codepage latin/CP858
  parts.push(0x1B, 0x74, 0x13)
  // Bold + double height + double width
  parts.push(0x1B, 0x21, 0x38)
  for (const b of enc.encode('NINTAIGO\n')) parts.push(b)
  // Normal font
  parts.push(0x1B, 0x21, 0x00)
  for (const b of enc.encode('Bridge OK\n')) parts.push(b)
  for (const b of enc.encode('-'.repeat(32) + '\n')) parts.push(b)
  for (const b of enc.encode(`Impresora: ${printer.slice(0, 30)}\n`)) parts.push(b)
  for (const b of enc.encode(`Fecha: ${new Date().toLocaleString('es-MX')}\n`)) parts.push(b)
  for (const b of enc.encode('-'.repeat(32) + '\n')) parts.push(b)
  for (const b of enc.encode('Pagina de prueba\n\n\n')) parts.push(b)
  // Full cut
  parts.push(0x1D, 0x56, 0x00)

  const bytes = new Uint8Array(parts)
  const qs = new URLSearchParams({ printer, jobName: 'test-page' })
  const res = await bridgeFetch(`/v1/print?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: bytes,
  }, timeoutMs)
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Bridge devolvió HTTP ${res.status}: ${text.slice(0, 200)}`)
  }
}

// ── drawer kick ──────────────────────────────────────────────────────────────

export interface DrawerKickOpts {
  /** Impresora destino. Si se omite, se resuelve la default. */
  printer?: string | null
  /** Pin del cajón. ESC/POS soporta 2 o 5. Default 2. */
  pin?: 2 | 5
}

/** Abre el cajón — MODE-AWARE:
 *  - 'host'  → POST /v1/drawer/kick al loopback. SIN CAMBIOS (resuelve { ok } sin jobId).
 *  - 'relay' → encola kind 'drawer' con payload { pin }. El Bridge HOST lo dispara.
 *    Devuelve { ok, jobId } apenas entra a la cola para que el caller pueda seguir
 *    el progreso (trackRelayJob). En 'host' no hay jobId (es síncrono). */
export async function kickDrawer(opts: DrawerKickOpts = {}, timeoutMs = 2500): Promise<PrintResult> {
  if (isRelayMode()) {
    const stationId = requireStationId()
    const enq = await relayEnqueueJob({
      stationId,
      kind: 'drawer',
      payload: { pin: opts.pin ?? 2 },
      copies: 1,
      idempotencyKey: newIdempotencyKey('drawer'),
    })
    return { ok: true, jobId: enq.jobId }
  }
  const printer = await resolveDefaultPrinter(opts.printer ?? null)
  if (!printer) throw new Error('No hay impresora disponible para abrir cajón')
  const qs = new URLSearchParams({ printer })
  const res = await bridgeFetch(`/v1/drawer/kick?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin: opts.pin ?? 2 }),
  }, timeoutMs)
  if (!res.ok) throw new Error(`Bridge devolvió HTTP ${res.status}`)
  return { ok: true }
}

// ── print ────────────────────────────────────────────────────────────────────

export interface PrintReceiptOpts {
  /** Impresora destino. Si se omite, se resuelve la default (solo mode 'host'). */
  printer?: string | null
  /** mode 'relay': copias a imprimir (el bridge repite el job). Default 1.
   *  En 'host' el conteo de copias ya va dentro del CanonicalReceipt/flujo del caller. */
  copies?: number
  /** mode 'relay': clase de impresión para enrutar/diagnosticar. Default 'receipt'. */
  kind?: Extract<RelayJobKind, 'receipt' | 'kitchen'>
}

/** Imprime un CanonicalReceipt — MODE-AWARE:
 *  - 'host'  → POST /v1/print/receipt al loopback (el bridge lo convierte a ESC/POS).
 *    Fast path histórico, SIN CAMBIOS.
 *  - 'relay' → encola `POST …/print/jobs` (kind receipt/kitchen, payload=CanonicalReceipt).
 *    Devuelve { ok, jobId } apenas entra a la cola; el papel sale cuando el Bridge
 *    HOST jala el job. */
export async function printReceipt(receipt: CanonicalReceipt, opts: PrintReceiptOpts = {}, timeoutMs = 5000): Promise<PrintResult> {
  if (isRelayMode()) {
    const stationId = requireStationId()
    const kind = opts.kind ?? 'receipt'
    const enq = await relayEnqueueJob({
      stationId,
      kind,
      payload: receipt,
      copies: opts.copies ?? 1,
      idempotencyKey: newIdempotencyKey(kind),
    })
    return { ok: true, jobId: enq.jobId }
  }
  const printer = await resolveDefaultPrinter(opts.printer ?? null)
  if (!printer) throw new Error('No hay impresora disponible')
  const qs = new URLSearchParams({ printer })
  const res = await bridgeFetch(`/v1/print/receipt?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(receipt),
  }, timeoutMs)
  if (!res.ok) throw new Error(`Bridge devolvió HTTP ${res.status}`)
  const json = await res.json().catch(() => ({})) as { ok?: boolean; jobId?: string; error?: string }
  return { ok: json.ok ?? true, ...(json.jobId ? { jobId: json.jobId } : {}), ...(json.error ? { error: json.error } : {}) }
}

/** Convierte bytes a base64 (para transportar ESC/POS pre-rendereado en JSON al relay). */
function bytesToBase64(u8: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i] as number)
  if (typeof btoa !== 'undefined') return btoa(bin)
  // Fallback no-browser (tests SSR): Buffer global si existe.
  const B = (globalThis as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } }).Buffer
  if (B) return B.from(bin, 'binary').toString('base64')
  throw new Error('No hay codificador base64 disponible')
}

/** Imprime bytes raw (ESC/POS pre-rendereado) — MODE-AWARE:
 *  - 'host'  → POST /v1/print (octet-stream) al loopback. SIN CAMBIOS.
 *  - 'relay' → encola kind 'raw' con payload { rawB64, jobName? } (los bytes van en
 *    base64 para sobrevivir el transporte JSON). */
export async function printRaw(bytes: Uint8Array | ArrayBuffer, opts: { printer?: string | null; jobName?: string; copies?: number } = {}, timeoutMs = 5000): Promise<PrintResult> {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  if (isRelayMode()) {
    const stationId = requireStationId()
    const enq = await relayEnqueueJob({
      stationId,
      kind: 'raw',
      payload: { rawB64: bytesToBase64(u8), ...(opts.jobName ? { jobName: opts.jobName } : {}) },
      copies: opts.copies ?? 1,
      idempotencyKey: newIdempotencyKey('raw'),
    })
    return { ok: true, jobId: enq.jobId }
  }
  const printer = await resolveDefaultPrinter(opts.printer ?? null)
  if (!printer) throw new Error('No hay impresora disponible')
  const qs = new URLSearchParams({ printer })
  if (opts.jobName) qs.set('jobName', opts.jobName)
  // Wrap en Blob para satisfacer BodyInit (Uint8Array<ArrayBufferLike> no es
  // asignable directo a BodyInit en algunas versiones de @types/node-dom).
  const body = new Blob([u8 as unknown as BlobPart], { type: 'application/octet-stream' })
  const res = await bridgeFetch(`/v1/print?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body,
  }, timeoutMs)
  if (!res.ok) throw new Error(`Bridge devolvió HTTP ${res.status}`)
  const json = await res.json().catch(() => ({})) as { ok?: boolean; jobId?: string; error?: string }
  return { ok: json.ok ?? true, ...(json.jobId ? { jobId: json.jobId } : {}), ...(json.error ? { error: json.error } : {}) }
}

export const bridgeClient = {
  getStatus,
  getPrinters,
  resolveDefaultPrinter,
  testPage,
  kickDrawer,
  printReceipt,
  printRaw,
}
export default bridgeClient
